import Vue from 'vue';
import Vuex from 'vuex';

import mutations from '@/js/store/calculator/mutations';
import actions from '@/js/store/calculator/actions';
import getters from '@/js/store/calculator/getters';
import initialState from '@/js/store/calculator/initialState';
import { generateReserveComponent } from '@/js/store/calculator/utilities/reserveComponentHelpers';
import { getResultsOverviewInTodaysDollars, getResultsOverviewInFutureDollars } from '@/js/store/calculator/utilities/overviewHelpers';
import { getResultsTspSummaryInTodaysDollars, getResultsTspSummaryInFutureDollars } from '@/js/store/calculator/utilities/tspSummaryHelpers';

import Hi3ServiceMember from './state/Hi3ServiceMember';
import { getOverrideRanksForHi3PastYears } from './utilities/hi3CareerProgressionHelpers';
import {
  initializeValues,
  getAllNumbersByMonth,
  generateHi3Pension,
  generateServiceMemberTsp,
  getAllNumbersByMonthFromRunDateToDeathDateTodaysDollars,
  getAllNumbersByMonthFromRunDateToDeathDateFutureDollars,
} from './utilities/hi3AllNumbersHelpers';
import { getResultsAllPaymentsInTodaysDollars, getResultsAllPaymentsInFutureDollars } from './utilities/hi3AllPaymentsHelpers';

Vue.use(Vuex);

export default (preloadedState, useAll, config) => new Vuex.Store({
  state: useAll
    ? preloadedState
    : {
      ...preloadedState,
      ...initialState,
      config,
    },

  mutations: {
    ...mutations,

    updateYearOfServiceRankHi3PastYears(state, { yearOfServiceRank, index }) {
      state.serviceMember.careerProgression.hi3PastYears[index].yearOfServiceRank = yearOfServiceRank;
    },

    updateOverrideRanksHi3PastYears(state, { yearOfServiceRank, index }) {
      const careerProgressionHi3PastYears = state.serviceMember.careerProgression.hi3PastYears;
      for (let i = index; i < careerProgressionHi3PastYears.length; i += 1) {
        const currentItem = careerProgressionHi3PastYears[i];
        const overrideRanks = getOverrideRanksForHi3PastYears(
          yearOfServiceRank,
          currentItem.yearOfService,
        );
        currentItem.overrideRanks = overrideRanks;
      }
    },

    generateResults(state) {
      state.serviceMember.results.allNumbersByMonth = getAllNumbersByMonth(state.serviceMember);

      if (state.serviceMember.componentMatrix() !== state.serviceMember.COMPONENT_MATRICES.AC_2_AC) {
        state.serviceMember = generateReserveComponent(state.serviceMember);
      }

      if (state.serviceMember.pensionRetirementType() !== state.serviceMember.RETIREMENT_ELIGIBILITIES.TSP_ONLY) {
        state.serviceMember.results.allNumbersByMonth = generateHi3Pension(state.serviceMember);
      }

      state.serviceMember.results.allNumbersByMonth = initializeValues(state.serviceMember);
      state.serviceMember.results.todaysDollars.overview = generateServiceMemberTsp(state.serviceMember, state.formData);

      state.serviceMember.results.todaysDollars.overview = getResultsOverviewInTodaysDollars(state.serviceMember, state.moduleSettings);
      state.serviceMember.results.futureDollars.overview = getResultsOverviewInFutureDollars(state.serviceMember, state.moduleSettings);
      state.serviceMember.results.todaysDollars.tspSummary = getResultsTspSummaryInTodaysDollars(state.serviceMember);
      state.serviceMember.results.futureDollars.tspSummary = getResultsTspSummaryInFutureDollars(state.serviceMember);
      state.serviceMember.results.todaysDollars.allPayments = getResultsAllPaymentsInTodaysDollars(state.serviceMember);
      state.serviceMember.results.futureDollars.allPayments = getResultsAllPaymentsInFutureDollars(state.serviceMember);
      state.serviceMember.results.todaysDollars.allNumbers = getAllNumbersByMonthFromRunDateToDeathDateTodaysDollars(state.serviceMember);
      state.serviceMember.results.futureDollars.allNumbers = getAllNumbersByMonthFromRunDateToDeathDateFutureDollars(state.serviceMember);
    },
  },

  getters,

  actions: {
    ...actions,

    clearServiceMember({ commit }) {
      commit('setServiceMember', new Hi3ServiceMember());
    },

    initializeServiceMember({ commit, state }) {
      const { grade, retirementSTMonths } = state.formData;

      let serviceMember;
      if (grade && retirementSTMonths) {
        serviceMember = new Hi3ServiceMember(state.formData, state.globalSettings, state.payTables, state.moduleSettings);
      } else {
        serviceMember = new Hi3ServiceMember();
      }

      commit('setServiceMember', serviceMember);
    },

    updateYearOfServiceRankHi3PastYears({ commit }, { yearOfServiceRank, index }) {
      commit('updateYearOfServiceRankHi3PastYears', { yearOfServiceRank, index });
    },

    updateOverrideRanksHi3PastYears({ commit }, { yearOfServiceRank, index }) {
      commit('updateOverrideRanksHi3PastYears', { yearOfServiceRank, index });
    },

    generateResults({ commit }) {
      commit('generateResults');
    },
  },
});
